import { useAccess } from '@umijs/max';
import { Tooltip } from 'antd';
import React from 'react';

export interface PermissionWrapperProps {
  /** 权限码，支持单个或数组 (如 'cmsMaterial:upload' 或 ['cmsMaterial:view', 'cmsMaterial:upload']) */
  code?: string | string[];
  /** 无权限时的提示语，默认为“您暂无该操作权限，请联系管理员分配” */
  tooltip?: React.ReactNode;
  /** 是否在无权限时直接隐藏，默认为 false（置灰 + Tooltip） */
  hideWhenNoPermission?: boolean;
  /** 外部额外传入的 disabled 状态 */
  disabled?: boolean;
  children: React.ReactElement<any>;
}

export const PermissionWrapper: React.FC<PermissionWrapperProps> = ({
  code,
  tooltip = '您暂无该操作权限，请联系管理员分配',
  hideWhenNoPermission = false,
  disabled: externalDisabled,
  children,
}) => {
  const access = useAccess() as Record<string, boolean | undefined>;

  const hasPermission = React.useMemo(() => {
    if (!code) return true;
    const codes = Array.isArray(code) ? code : [code];
    return codes.every((c) => Boolean(access[c]));
  }, [code, access]);

  if (hasPermission && !externalDisabled) {
    return children;
  }

  if (!hasPermission && hideWhenNoPermission) {
    return null;
  }

  const tooltipText = !hasPermission ? tooltip : undefined;

  if (React.isValidElement(children)) {
    let targetElement = children;
    if (React.isValidElement(children.props?.children)) {
      targetElement = children.props.children;
    }
    const childElement = targetElement as React.ReactElement<{
      disabled?: boolean;
      style?: React.CSSProperties;
    }>;
    const disabledChild = React.cloneElement(childElement, {
      disabled: true,
      onClick: undefined,
    });

    if (tooltipText) {
      return (
        <Tooltip title={tooltipText}>
          <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
            {disabledChild}
          </span>
        </Tooltip>
      );
    }

    return disabledChild;
  }

  return children;
};

export default PermissionWrapper;
